import { ProList } from '@ant-design/pro-components'
import { Card, Flex, Pagination, Typography } from 'antd'
import type { ChangeEventHandler, ComponentProps, PropsWithChildren, ReactNode } from 'react'
import { CreateButton } from '../button'
import { SearchInput } from '../common'

export type ListSelectProps<T extends Record<string, unknown>> = Omit<
  ComponentProps<typeof ProList<T>>,
  'search' | 'title'
> & {
  title?: string | ReactNode
  search?: {
    value: string
    onChange: ChangeEventHandler<HTMLInputElement, HTMLInputElement>
  }
  pagination?: ComponentProps<typeof Pagination>
  addItem?: {
    label: string
    onClick: React.MouseEventHandler<HTMLElement>
  }
  cardProps?: Omit<ComponentProps<typeof Card>, 'children'>
}

export function ListSelect<T extends Record<string, unknown>>({
  title,
  search,
  pagination,
  addItem,
  cardProps,
  ...proListProps
}: ListSelectProps<T>) {
  return (
    <Flex vertical gap="small">
      {search && (
        <Flex align="center" justify="space-between" gap="small">
          {title && <Typography.Title level={5}>{title}</Typography.Title>}
          <div className="w-full sm:w-1/2 md:w-1/3 self-end">
            <SearchInput {...search} />
          </div>
        </Flex>
      )}
      <CardWrapper {...cardProps}>
        <ProList<T> size="small" ghost split tableAlertRender={false} {...proListProps} />
      </CardWrapper>
      {addItem && <CreateButton onClick={addItem.onClick}>{addItem.label}</CreateButton>}
      {pagination && (
        <div className="self-end">
          <Pagination defaultCurrent={1} defaultPageSize={10} {...pagination} />
        </div>
      )}
    </Flex>
  )
}

export interface CardWrapperProps extends ComponentProps<typeof Card> {
  children: React.ReactNode
  innerCardProps?: Omit<ComponentProps<typeof Card.Grid>, 'children'>
}

export function CardWrapper({ children, innerCardProps, ...cardProps }: CardWrapperProps) {
  return (
    <Card size="small" variant="borderless" {...cardProps}>
      <Card.Grid style={{ width: '100%', padding: 4 }} hoverable={false} {...innerCardProps}>
        {children}
      </Card.Grid>
    </Card>
  )
}

export interface InputListProps extends Pick<ComponentProps<typeof ListSelect>, 'title'> {
  items: {
    label: ReactNode
    children: ReactNode
  }[]
}

export function InputList({ items, title }: InputListProps) {
  return (
    <ListSelect
      cardProps={{ title }}
      className="[&_.ant-pro-list-row-header]:max-w-1/4!"
      dataSource={items}
      columns={[
        { listSlot: 'title', dataIndex: 'label' },
        { listSlot: 'content', dataIndex: 'children' },
      ]}
    />
  )
}

export function RequiredFieldLabel({ children, htmlFor }: PropsWithChildren<{ htmlFor?: string }>) {
  return (
    <label htmlFor={htmlFor} className="font-bold">
      {children}
      <Typography.Text type="danger">*</Typography.Text>
    </label>
  )
}
